/**
 * Goal List Component
 *
 * Displays a list of goals with optional grouping.
 */

import { useMemo } from 'react';
import type { Goal, GoalGroupBy } from '@/types';
import { GoalCard } from './goal-card';

interface GoalListProps {
  goals: Goal[];
  isLoading?: boolean;
  groupBy?: GoalGroupBy;
  onEdit?: (goal: Goal) => void;
  onComplete?: (goalId: string) => void;
  onReopen?: (goalId: string) => void;
  onDelete?: (goalId: string) => void;
  onClick?: (goal: Goal) => void;
  emptyMessage?: string;
}

const groupLabels: Record<GoalGroupBy, Record<string, string>> = {
  status: {
    active: '🎯 Active',
    completed: '✅ Completed',
    paused: '⏸️ Paused',
    abandoned: '❌ Abandoned',
  },
  type: {
    outcome: '🎯 Outcomes',
    process: '🔄 Processes',
    milestone: '🏁 Milestones',
  },
  value: {}, // Dynamic based on value names
  targetDate: {
    overdue: '🔴 Overdue',
    thisWeek: '📅 This Week',
    thisMonth: '🗓️ This Month',
    later: '📦 Later',
    noDate: '📭 No Target Date',
  },
  relationships: {}, // Dynamic based on related goal names
};

function groupGoals(goals: Goal[], groupBy: GoalGroupBy): Record<string, Goal[]> {
  const groups: Record<string, Goal[]> = {};

  goals.forEach((goal) => {
    let key: string;

    switch (groupBy) {
      case 'status':
        key = goal.status;
        break;
      case 'type':
        key = goal.goalType;
        break;
      case 'value':
        // Group by first value or 'No Value'
        key = goal.valueIds && goal.valueIds.length > 0 ? goal.valueIds[0] : 'no_value';
        break;
      case 'targetDate': {
        if (!goal.targetDate) {
          key = 'noDate';
        } else {
          const target = new Date(goal.targetDate);
          const now = new Date();
          const diffDays = Math.floor((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          
          if (diffDays < 0) key = 'overdue';
          else if (diffDays <= 7) key = 'thisWeek';
          else if (diffDays <= 30) key = 'thisMonth';
          else key = 'later';
        }
        break;
      }
      case 'relationships': {
        // Group by parent goals (if any)
        const parentRels = goal.relatedGoals?.filter(r => r.type === 'parent') || [];
        key = parentRels.length > 0 ? parentRels[0].goalId : 'top_level';
        break;
      }
      default:
        key = 'all';
    }

    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(goal);
  });

  return groups;
}

function getGroupLabel(groupBy: GoalGroupBy, key: string): string {
  if (groupBy === 'value') {
    if (key === 'no_value') return '💫 Not Aligned to Value';
    // Could look up value name from context/store
    return `💎 ${key}`;
  }
  if (groupBy === 'relationships') {
    if (key === 'top_level') return '🏔️ Top-Level Goals';
    // Could look up parent goal name from context/store
    return `📁 Sub-goal of: ${key}`;
  }
  return groupLabels[groupBy]?.[key] || key;
}

export function GoalList({
  goals,
  isLoading = false,
  groupBy,
  onEdit,
  onComplete,
  onReopen,
  onDelete,
  onClick,
  emptyMessage = 'No goals found',
}: GoalListProps) {
  const groupedGoals = useMemo(() => {
    if (!groupBy) return null;
    return groupGoals(goals, groupBy);
  }, [goals, groupBy]);

  if (isLoading) {
    return (
      <div className="p-8 text-center" data-testid="goal-list-loading">
        <div className="inline-flex items-center gap-2 text-gray-500">
          <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Loading goals...
        </div>
      </div>
    );
  }

  if (goals.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500" data-testid="goal-list-empty">
        <svg
          className="mx-auto w-12 h-12 text-gray-300 mb-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Render grouped
  if (groupedGoals) {
    const groupKeys = Object.keys(groupedGoals);

    // Sort groups logically
    if (groupBy === 'status') {
      const statusOrder = ['active', 'paused', 'completed', 'abandoned'];
      groupKeys.sort((a, b) => statusOrder.indexOf(a) - statusOrder.indexOf(b));
    } else if (groupBy === 'type') {
      const typeOrder = ['outcome', 'process', 'milestone'];
      groupKeys.sort((a, b) => typeOrder.indexOf(a) - typeOrder.indexOf(b));
    } else if (groupBy === 'targetDate') {
      const dateOrder = ['overdue', 'thisWeek', 'thisMonth', 'later', 'noDate'];
      groupKeys.sort((a, b) => dateOrder.indexOf(a) - dateOrder.indexOf(b));
    }

    return (
      <div className="space-y-6">
        {groupKeys.map((key) => (
          <div key={key}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 px-1">
              {getGroupLabel(groupBy!, key)}
              <span className="ml-2 text-gray-400">({groupedGoals[key].length})</span>
            </h3>
            <div className="space-y-3">
              {groupedGoals[key].map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onEdit={onEdit}
                  onComplete={onComplete}
                  onReopen={onReopen}
                  onDelete={onDelete}
                  onClick={onClick}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Render flat list
  return (
    <div className="space-y-3" data-testid="goal-list">
      {goals.map((goal) => (
        <GoalCard
          key={goal.id}
          goal={goal}
          onEdit={onEdit}
          onComplete={onComplete}
          onReopen={onReopen}
          onDelete={onDelete}
          onClick={onClick}
        />
      ))}
    </div>
  );
}

